import { squish } from '../../../services/helpers/OnSiteOrder';

/**
 * Pure rules of the tables list (Order Methods → Table ordering, PROD-724). See
 * tests/components/takeout/tables/tableList.utils.test.js.
 */

/** From this many tables the list gets a search field and scrolls in its own box. */
export const SEARCH_FROM = 15;

/** Card widths: one line per table from 840 px, two lines below, the phone layout up to 420 px. */
export const WIDE_FROM = 840;
export const PHONE_UP_TO = 420;

export const TableListLayout = Object.freeze({
  Wide: 'wide',
  Narrow: 'narrow',
  Phone: 'phone',
});

/** The longest name the backend takes (`max:255`, counted after squishing, in characters). */
export const TABLE_NAME_MAX = 255;

/** As the backend stores a name: no spaces at the ends, one between words. */
export const cleanTableName = squish;

/** The form names are compared in: case and extra spaces don't count. */
export function normaliseTableName(name) {
  return cleanTableName(name).toLowerCase();
}

/**
 * Why a name can't be used, or null: `{ code: 'empty' }`, `{ code: 'too_long', max }`, or
 * `{ code: 'duplicate', name }` with the name of the table that has it. `except` is the table being
 * renamed.
 */
export function tableNameError(name, tables, except = null) {
  const normalised = normaliseTableName(name);

  if (!normalised) {
    return { code: 'empty' };
  }

  // Characters, not UTF-16 units, as the backend counts them.
  if ([...normalised].length > TABLE_NAME_MAX) {
    return { code: 'too_long', max: TABLE_NAME_MAX };
  }

  const taken = (tables ?? []).find(
    (table) => table.id !== except?.id && normaliseTableName(table.name) === normalised,
  );

  return taken ? { code: 'duplicate', name: taken.name } : null;
}

/** The tables whose name contains the search text. */
export function filterTables(tables, query) {
  const needle = normaliseTableName(query);

  if (!needle) {
    return tables;
  }

  return tables.filter((table) => normaliseTableName(table.name).includes(needle));
}

/** The layout for the card's width. Zero (not measured yet, or jsdom) is the wide layout. */
export function layoutForWidth(width) {
  if (!width || width >= WIDE_FROM) {
    return TableListLayout.Wide;
  }

  return width > PHONE_UP_TO ? TableListLayout.Narrow : TableListLayout.Phone;
}

/** The list with the table at `from` moved to `to`; the same list when either is out of range. */
export function moveTable(tables, from, to) {
  if (from === to || from < 0 || to < 0 || from >= tables.length || to >= tables.length) {
    return tables;
  }

  const next = [...tables];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);

  return next;
}

/** What a screen reader hears after a move: "“1” moved to position 2 of 8." */
export function reorderAnnouncement(t, tables, id) {
  const position = tables.findIndex((table) => table.id === id);

  if (position === -1) {
    return '';
  }

  return t('announcements.moved', { name: tables[position].name, position: position + 1, count: tables.length });
}

/** The link as shown under a table's name, without the scheme. */
export function displayLink(link) {
  return (link ?? '').replace(/^https?:\/\//, '');
}
